import { useEffect, useMemo, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import FilterBar from './components/FilterBar.jsx';
import ProviderMap from './components/ProviderMap.jsx';
import ProviderList from './components/ProviderList.jsx';
import EmptyState from './components/EmptyState.jsx';
import { applyFilters, shuffle } from './lib/filters.js';

const RESULTS_PER_PAGE = 5;
const DEFAULT_RADIUS_MILES = 30;
const RADIUS_OPTIONS = [30, 50];

// Default map center when no search has been entered yet. Long Island NY,
// matching the legacy build (line 2371 of pf-shopping.js — `uluru` const).
const DEFAULT_MAP_CENTER = { lat: 40.7836271, lng: -73.4084305 };

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
// Fetch URL for the provider data. Defaults to /providers.json (works for
// `vite dev` from the public/ folder). In production build, .env.production
// overrides this to the HubSpot Files path.
const PROVIDERS_URL = import.meta.env.VITE_PROVIDERS_URL || '/providers.json';

export default function App() {
  // Data — loaded once from public/providers.json
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // User input state
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  const [radius, setRadius] = useState(DEFAULT_RADIUS_MILES);
  const [searchCenter, setSearchCenter] = useState(null); // { lat, lng } once a ZIP/address resolves

  // Derived: full filtered list (used for Refresh re-shuffle) + visible-5 subset
  const [allMatching, setAllMatching] = useState([]);
  const [visible, setVisible] = useState([]);
  const [highlightedKey, setHighlightedKey] = useState(null); // for list↔map sync

  // Load the data once
  useEffect(() => {
    fetch(PROVIDERS_URL)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setData)
      .catch(err => setLoadError(err.message));
  }, []);

  // Sorted categories list (display order = line_position from the # column)
  const categories = useMemo(() => {
    if (!data) return [];
    return [...data.categories].sort((a, b) => a.line_position - b.line_position);
  }, [data]);

  // Selected category record (or null if patient hasn't picked yet — drives empty state)
  const selectedCategory = useMemo(() => {
    if (!selectedCategoryName) return null;
    return categories.find(c => c.name === selectedCategoryName) || null;
  }, [categories, selectedCategoryName]);

  // Re-run filter+shuffle pipeline whenever inputs change.
  // Location is the only required input; device is an optional refinement.
  useEffect(() => {
    if (!data || !searchCenter) {
      setAllMatching([]);
      setVisible([]);
      return;
    }
    const filtered = applyFilters(data.providers, selectedCategory, searchCenter, radius);
    setAllMatching(filtered);
    setVisible(filtered.slice(0, RESULTS_PER_PAGE));
    setHighlightedKey(null);
  }, [data, selectedCategory, searchCenter, radius]);

  /** Refresh button: reshuffle the same in-range set, show a fresh 5 */
  function handleRefresh() {
    if (allMatching.length === 0) return;
    const reshuffled = shuffle(allMatching);
    setAllMatching(reshuffled);
    setVisible(reshuffled.slice(0, RESULTS_PER_PAGE));
    setHighlightedKey(null);
  }

  if (loadError) {
    return (
      <div className="pf-app pf-app--error">
        <div className="pf-error">
          Sorry, the provider directory couldn't load. ({loadError})
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pf-app pf-app--loading">
        <div className="pf-loading">Loading…</div>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="pf-app pf-app--error">
        <div className="pf-error">
          Google Maps API key not configured. Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>.env.local</code>.
        </div>
      </div>
    );
  }

  const showResults = Boolean(searchCenter);
  const moreAvailable = allMatching.length > RESULTS_PER_PAGE;
  const mapCenter = searchCenter ?? DEFAULT_MAP_CENTER;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <div className="pf-app">
        <FilterBar
          categories={categories}
          selectedCategoryName={selectedCategoryName}
          onCategoryChange={setSelectedCategoryName}
          radius={radius}
          onRadiusChange={setRadius}
          radiusOptions={RADIUS_OPTIONS}
          onLocationChange={setSearchCenter}
        />

        {/* Map always renders on page load (matches legacy build behavior).
            List column shows the empty-state prompt until the patient has
            picked both a device + a location. */}
        <div className="pf-results">
          <div className="pf-results__list">
            {!showResults ? (
              <EmptyState hasLocation={Boolean(searchCenter)} />
            ) : visible.length === 0 ? (
              <div className="pf-noresults">
                No practices match your search. Try a larger radius or a different device.
              </div>
            ) : (
              <>
                {moreAvailable && (
                  <button
                    className="pf-refresh"
                    // Prevent focus shift on mouse click. Mouse users never
                    // get a focus ring on this button (it's a trigger-action
                    // button, not navigation — they don't need re-activation
                    // after click). Keyboard users get focus via Tab and the
                    // proper :focus-visible ring fires for accessibility.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleRefresh}
                  >
                    Refresh now to view 5 more practices near you
                  </button>
                )}
                <ProviderList
                  providers={visible}
                  highlightedKey={highlightedKey}
                  onHighlight={setHighlightedKey}
                />
              </>
            )}
          </div>
          <div className="pf-results__map">
            <ProviderMap
              providers={visible}
              center={mapCenter}
              highlightedKey={highlightedKey}
              onHighlight={setHighlightedKey}
            />
          </div>
        </div>
      </div>
    </APIProvider>
  );
}
